"use client";

/**
 * The bar across the top of Klip's pages (not the editor, which has its
 * own): where she is, the page's own actions, notifications and her
 * account. Same shape as OpenCut's projects header.
 */

import Link from "next/link";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import {
	DashboardSquare01Icon,
	Logout01Icon,
	Notification03Icon,
	Settings01Icon,
} from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/utils/ui";
import { api, type Notification } from "../api";
import { useKlip } from "../data";
import { useSession } from "../providers";
import { Avatar, Icon, when } from "../ui";

export function KlipMark({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 32 32"
			aria-hidden="true"
			className={cn("size-6 shrink-0", className)}
		>
			<rect width="32" height="32" rx="9" className="fill-foreground" />
			<path
				d="M12.5 10.2v11.6c0 .8.9 1.3 1.6.8l8.4-5.8c.6-.4.6-1.2 0-1.6l-8.4-5.8c-.7-.5-1.6 0-1.6.8z"
				className="fill-background"
			/>
		</svg>
	);
}

export function AppHeader({
	children,
	actions,
	className,
}: {
	/** Where she is: a title or a breadcrumb. */
	children: ReactNode;
	actions?: ReactNode;
	className?: string;
}) {
	return (
		<header
			className={cn(
				"bg-background sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 px-4 sm:px-6",
				className,
			)}
		>
			<Link
				href="/"
				className="flex items-center gap-2 rounded-md"
				aria-label="Klip · הפרויקטים שלי"
			>
				<KlipMark />
			</Link>
			<div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
			<div className="flex items-center gap-2">
				{actions}
				<NotificationsButton />
				<AccountMenu />
			</div>
		</header>
	);
}

export function NotificationsButton() {
	const router = useRouter();
	const list = useKlip({
		key: "notifications",
		fetcher: api.notifications,
		when: (event) => event.type === "notification",
	});
	const unread = groupNotifications(list.data ?? []).filter((g) => g.some((n) => !n.read)).length;

	return (
		<DropdownMenu
			onOpenChange={(open) => {
				if (!open && unread) void api.markRead().then(list.refetch);
			}}
		>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative size-8"
					aria-label={unread ? `התראות, ${unread} חדשות` : "התראות"}
				>
					<Icon icon={Notification03Icon} />
					{unread > 0 && (
						<span className="bg-pending text-pending-foreground absolute -top-0.5 -end-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[0.625rem] font-bold">
							{unread}
						</span>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-80 p-1.5">
				<DropdownMenuLabel className="text-sm">התראות</DropdownMenuLabel>
				{(list.data ?? []).length === 0 ? (
					<div className="text-muted-foreground px-2 py-6 text-center text-sm">
						אין התראות חדשות
					</div>
				) : (
					groupNotifications(list.data ?? []).slice(0, 12).map((g) => {
						const [n, ...more] = g;
						const fresh = g.some((x) => !x.read);
						return (
							<DropdownMenuItem
								key={n!.id}
								className="flex flex-col items-start gap-0.5 py-2"
								onSelect={() => {
									if (n!.href) router.push(klipHref(n!.href));
								}}
							>
								<span className={cn("text-sm leading-snug", fresh ? "text-foreground" : "text-muted-foreground")}>
									{fresh && <span className="bg-pending me-1.5 inline-block size-1.5 rounded-full align-middle" />}
									{n!.text}
								</span>
								{more.slice(0, 2).map((m) => (
									<span key={m.id} className="text-muted-foreground text-xs leading-snug">
										{m.text}
									</span>
								))}
								{more.length > 2 && <span className="text-subtle-foreground text-xs">ועוד {more.length - 2} כאלה</span>}
								<span className="text-subtle-foreground text-xs">{when(n!.at)}</span>
							</DropdownMenuItem>
						);
					})
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/**
 * Notifications about the same thing close together are one: the same kind
 * and link within half an hour (a clip posted to three platforms, a run of
 * alerts), newest first.
 */
export function groupNotifications(list: Notification[]): Notification[][] {
	const groups: Notification[][] = [];
	for (const n of list) {
		const g = groups.at(-1);
		const last = g?.at(-1);
		if (g && last && last.kind === n.kind && last.href === n.href && last.at - n.at < 30 * 60_000) g.push(n);
		else groups.push([n]);
	}
	return groups;
}

/** The server links to /sources/:id; the interface calls that page /r/:id. */
export function klipHref(href: string): string {
	return href.replace(/^\/sources\//, "/r/").replace(/^\/projects$/, "/");
}

export function AccountMenu() {
	const { user, status } = useSession();
	const router = useRouter();
	if (!user) return null;

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="rounded-full"
					aria-label={`החשבון של ${user.name}`}
				>
					<Avatar name={user.name} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuLabel className="flex flex-col">
					<span className="text-sm">{user.name}</span>
					<span className="text-subtle-foreground text-xs font-normal">
						{user.email}
					</span>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={() => router.push("/settings")}>
					<Icon icon={Settings01Icon} />
					הגדרות
				</DropdownMenuItem>
				{user.role === "admin" && (
					<DropdownMenuItem onSelect={() => router.push("/admin")}>
						<Icon icon={DashboardSquare01Icon} />
						ניהול
					</DropdownMenuItem>
				)}
				{(status?.google || status?.devSignIn) && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							onSelect={async () => {
								await api.signOut();
								window.location.href = "/login";
							}}
						>
							<Icon icon={Logout01Icon} />
							יציאה
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
